import React from 'react'
import { Gem, Zap } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { parseSkinName, formatPrice } from '../../../utils/formatters'
import {
  accentColor, cardPrimaryColor, skinCardBgColor, skinCardPrimaryColor
} from '../utils/profileColors'

/**
 * Цвет редкости скина (color из API, "#rrggbb" или "rrggbb") → rgba-строка.
 * Без валидного цвета — fallback (например, нейтральная рамка).
 */
const rarityAlpha = (color, alpha, fallback = `rgba(255, 255, 255, ${alpha})`) => {
  const hex = typeof color === 'string' ? color.replace('#', '') : ''
  if (!/^[0-9a-fA-F]{6}$/.test(hex)) return fallback
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * Карточка скина публичного профиля (топ скинов и инвентарь), дизайн «Неоновая
 * призма»: тонировка и рамка цветом редкости, свечение под картинкой, ранг-бейдж,
 * чип множителя (только top_skins, в инвентаре множителя нет).
 * Кастомизация профиля: card_bg — основа фона под градиентом редкости,
 * accent — чип множителя; название, подпись, цена и Gem адаптируются к card_bg.
 * @param {object} item - { name, img, color, game, cost, multiplier? }
 * @param {object} colors - цвета профиля (card_bg, accent)
 * @param {number} rank - позиция в топе (1-based); топ-1 — золотой бейдж
 * @param {function} [onClick] - если передан, карточка кликабельна (opened showcase)
 */
export const ProfileSkinCard = ({ item, colors, rank, onClick }) => {
  const skin = parseSkinName(item.name)
  const accent = accentColor(colors)
  const primary = skinCardPrimaryColor(colors)
  const cardBg = skinCardBgColor(colors)
  const isTop = rank === 1

  return (
    <div
      onClick={onClick}
      className={`group relative overflow-hidden rounded-2xl border transition-all ${
        onClick ? 'cursor-pointer active:scale-[0.98]' : ''
      }`}
      style={{
        borderColor: rarityAlpha(item.color, 0.4, 'rgba(255, 255, 255, 0.1)'),
        background: `linear-gradient(to bottom, ${rarityAlpha(item.color, 0.14)}, ${rarityAlpha(item.color, 0.03)}), ${cardBg}`,
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.07)'
      }}
    >
      {/* Свечение цвета редкости под скином */}
      <div
        className="pointer-events-none absolute left-1/2 top-1/3 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-70 blur-2xl"
        style={{ backgroundColor: rarityAlpha(item.color, 0.45, 'transparent') }}
      />

      {/* Бейдж ранга: топ-1 — золотой, остальные — нейтральные */}
      {rank != null && (
        <div
          className={`absolute left-2 top-2 z-10 rounded-md px-1.5 py-0.5 text-[10px] font-black italic tabular-nums ${
            isTop
              ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-dark-primary'
              : 'border'
          }`}
          style={isTop ? undefined : { backgroundColor: `${primary}14`, borderColor: `${primary}1F`, color: primary }}
        >
          #{rank}
        </div>
      )}

      {/* Множитель выигрыша (только у top_skins) */}
      {item.multiplier != null && (
        <div
          className="absolute right-2 top-2 z-10 flex items-center gap-0.5 rounded-full border border-white/10 bg-dark-primary/70 px-1.5 py-0.5 text-[10px] font-bold tabular-nums"
          style={{ color: accent }}
        >
          <Zap size={9} />
          ×{item.multiplier}
        </div>
      )}

      <div className="relative flex aspect-square items-center justify-center p-4 pt-6">
        {item.img ? (
          <img
            src={item.img}
            alt={item.name}
            loading="lazy"
            className="max-h-full max-w-full object-contain transition-transform duration-300 lg:group-hover:scale-105"
            onError={(e) => { e.target.style.display = 'none' }}
          />
        ) : null}
      </div>

      <div className="relative p-3 pt-0">
        <div className="truncate text-xs font-semibold leading-tight" style={{ color: primary }}>
          {skin.title}
        </div>
        {skin.subtitleCompact && (
          <div className="truncate text-[10px] leading-tight" style={{ color: primary }}>
            {skin.subtitleCompact}
          </div>
        )}

        {/* Цена и Gem используют тот же автоматически контрастный цвет, что название */}
        <div className="mt-2 flex items-center gap-1" style={{ color: primary }}>
          <Gem size={12} className="shrink-0" />
          <span className="text-sm font-bold tabular-nums">
            {formatPrice(item.cost)}
          </span>
        </div>
      </div>
    </div>
  )
}

/**
 * Сетка скинов. null ≠ []: пустой массив — пустое состояние, отсутствие блока
 * решает страница (по visibility), не сетка.
 * Если передан onItemClick — карточки становятся кликабельными (виральная
 * презентация скина SkinShowcaseModal), приходит (item, rank) с рангом 1-based.
 */
const ProfileSkinsGrid = ({ items, colors, emptyText, onItemClick }) => {
  const { t } = useTranslation()

  if (!items || items.length === 0) {
    return (
      <div className="py-6 text-center text-sm" style={{ color: cardPrimaryColor(colors) }}>
        {emptyText || t('publicProfile.noSkins', 'Пока нет скинов')}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {items.map((item, index) => (
        <ProfileSkinCard
          key={`${item.name}-${index}`}
          item={item}
          colors={colors}
          rank={index + 1}
          onClick={onItemClick ? () => onItemClick(item, index + 1) : undefined}
        />
      ))}
    </div>
  )
}

export default ProfileSkinsGrid
